import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

import { DocumentsTableRowData } from '../DocumentsTable.types';
import { useCopyDocumentOcrMarkdownActionItemLazyQuery } from './graphql/CopyDocumentOcrMarkdownActionItem.generated';

interface CopyDocumentOcrMarkdownActionItemProps
  extends Partial<MenuItemProps> {
  row: DocumentsTableRowData;
}

/**
 * @description This an action item component for use by the table ActionButtonRenderer
 */
export function CopyDocumentOcrMarkdownActionItem({
  row,
  ...menuItemProps
}: CopyDocumentOcrMarkdownActionItemProps) {
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, showFeedback } = useFeedback();
  const [copyOcrMarkdown, { loading }] =
    useCopyDocumentOcrMarkdownActionItemLazyQuery({
      variables: {
        documentId: row.id,
      },
      onError: createErrorFeedback('Failed to copy OCR markdown'),
      onCompleted: (data) => {
        // typeguard
        if (data?.node?.__typename !== 'Document') return;

        // protect against un-ocr'd docs
        if (!data.node.ocrExtractedMarkdownContent) {
          return showFeedback(`No OCR markdown content found`);
        }

        return copyToClipboard(
          data.node.ocrExtractedMarkdownContent,
          () =>
            showFeedback('OCR markdown copied to clipboard', {
              variant: 'success',
            }),
          () => showFeedback('Failed to copy to clipboard')
        );
      },
    });

  return (
    <MenuItem
      icon={<Copy07Icon size={18} />}
      loading={loading}
      label="Copy OCR markdown"
      onClick={() => copyOcrMarkdown()}
      {...menuItemProps}
    />
  );
}
